import type { Country } from "../../types/country";
import type { QuestionnaireFormValues } from "../../types/form";
import type { AnsweredQuestion, AnyQuestion, CountryAndTinAnswer } from "../../types/question";
import { replaceAll } from "../../utils/string";

// returns a flat array with questions and all subquestions to arbitrary depth
export const getAllSubQuestions = (questions: Array<AnyQuestion>): Array<AnyQuestion> =>
	questions.flatMap((question) => [
		question,
		...question.answerChoices.flatMap((answer) => getAllSubQuestions(answer.subQuestions)),
	]);

export const getCountryNameByCode = (code: string, countries: Array<Country>) =>
	countries.find((c) => c.alpha2Code === code)?.names.sv || ""; // todo: add other languages

export const formatFieldName = (name: string) => replaceAll(name, ".", "_");

export const restoreFieldName = (name: string) => replaceAll(name, "_", ".");

export const parseFormValues = (
	questions: Array<AnyQuestion>,
	values: QuestionnaireFormValues,
): Array<AnsweredQuestion> =>
	getAllSubQuestions(questions) // flatten question with subquestions array
		.map((question) => {
			const answers = values[formatFieldName(question.questionId)];
			if (!answers) return false;
			return {
				question: question.question,
				questionId: question.questionId,
				answers: answers,
			};
		})
		.filter((question) => !!question);

export const updateTinCountryList = (
	selectedCountryCodes: Array<string>,
	values: Array<CountryAndTinAnswer>,
) => {
	const previousCountryCodes = values.map((v) => v.answerValue.alpha2Code);

	const toAdd = selectedCountryCodes.filter((c) => !previousCountryCodes.includes(c));
	const toRemove = previousCountryCodes.filter((c) => !selectedCountryCodes.includes(c));

	const newAnswers: Array<CountryAndTinAnswer> = toAdd.map((a) => ({
		answerValue: {
			type: "CountryAndTin",
			alpha2Code: a,
			tin: "",
		},
	}));

	return [...values.filter((v) => !toRemove.includes(v.answerValue.alpha2Code)), ...newAnswers];
};

export const updateCountryTin = (
	alpha2Code: string,
	values: Array<CountryAndTinAnswer>,
	fieldValue: string,
) => {
	const tin: CountryAndTinAnswer = {
		answerValue: {
			alpha2Code,
			type: "CountryAndTin",
			tin: fieldValue,
		},
	};

	return values.map((previousTin) =>
		previousTin.answerValue.alpha2Code === alpha2Code ? tin : previousTin,
	);
};

export const getQuestionnaireInitialValues = (
	questions: Array<AnyQuestion>,
): QuestionnaireFormValues | undefined =>
	getAllSubQuestions(questions)
		.filter((question) => question.answers.length > 0)
		.reduce(
			(formValues, question) => ({
				...formValues,
				[formatFieldName(question.questionId)]: question.answers,
			}),
			{},
		);
